import { Star, User, BadgeCheck } from "lucide-react"
import { useState, useRef, useEffect } from "react"

interface ReviewCardProps {
  rating: number
  text: string
  username?: string
  avatarUrl?: string
  date?: Date
  isAnonymous?: boolean
  isPreview?: boolean
  isVerifiedClient?: boolean
  reviewUrls?: string[]
}

export function ReviewCard({ 
  rating, 
  text, 
  username = "Anonymous", 
  avatarUrl, 
  date = new Date(),
  isAnonymous = false,
  isPreview = false,
  isVerifiedClient = false,
  reviewUrls = []
}: ReviewCardProps) {
  const displayName = isAnonymous ? "Anonymous" : username
  const displayAvatar = isAnonymous ? null : avatarUrl
  const [isExpanded, setIsExpanded] = useState(false)
  const [needsTruncation, setNeedsTruncation] = useState(false)
  const textRef = useRef<HTMLParagraphElement>(null)

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }).format(date)
  }

  // Check if text needs truncation (more than 4 lines)
  useEffect(() => {
    if (textRef.current && text) {
      const lineHeight = parseInt(window.getComputedStyle(textRef.current).lineHeight)
      const height = textRef.current.scrollHeight
      const lines = Math.round(height / lineHeight)
      setNeedsTruncation(lines > 4)
    }
  }, [text])

  return (
    <div className={`rounded-[0.6rem] p-6 bg-card border border-border`}>
      {/* Header with stars and date */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex gap-1">
          {[1, 2, 3, 4, 5].map((star) => (
            <Star
              key={star}
              className={`w-4 h-4 ${
                star <= rating
                  ? "fill-[#FFA500] text-[#FFA500]"
                  : "text-muted-foreground"
              }`}
            />
          ))}
        </div>
        <span className="text-sm text-muted-foreground">
          {formatDate(date)}
        </span>
      </div>

      {/* Review text */}
      <div className="mb-4 relative">
        <p 
          ref={textRef}
          className={`text-foreground text-base whitespace-pre-wrap break-words ${
            needsTruncation && !isExpanded ? 'line-clamp-4' : ''
          }`}
        >
          {text || (isPreview ? "Your review will appear here..." : "")}
        </p>
        {needsTruncation && !isExpanded && (
          <div 
            className="absolute bottom-0 right-0 flex items-center cursor-pointer group"
            onClick={() => setIsExpanded(true)}
          >
            <div className="absolute right-0 bottom-0 h-6 w-32 bg-gradient-to-l from-card via-card/80 to-transparent pointer-events-none" />
            <span className="relative z-10 text-sm font-medium text-primary bg-card pl-2">
              More
            </span>
          </div>
        )}
        {needsTruncation && isExpanded && (
          <div className="flex justify-end mt-1">
            <span 
              className="text-sm font-medium text-primary cursor-pointer hover:underline"
              onClick={() => setIsExpanded(false)}
            >
              Less
            </span>
          </div>
        )}
      </div>

      {/* Review photos — aspect box avoids fixed short height (h-20) squishing wide cells */}
      {reviewUrls && reviewUrls.length > 0 && (
        <div className="mb-4 grid grid-cols-3 gap-2 sm:gap-3">
          {reviewUrls.map((url, index) => (
            <div
              key={index}
              className="relative aspect-square w-full min-h-0 overflow-hidden rounded-lg bg-muted"
            >
              <img
                src={url}
                alt={`Review photo ${index + 1}`}
                className="h-full w-full object-cover"
              />
            </div>
          ))}
        </div>
      )}

      {/* User info */}
      <div className="flex items-center gap-3">
        {/* Avatar */}
        <div className={`w-8 h-8 rounded-full overflow-hidden flex items-center justify-center ${
          isAnonymous ? 'bg-muted' : 'bg-muted'
        }`}>
          {displayAvatar ? (
            <img 
              src={displayAvatar} 
              alt={displayName}
              className="w-full h-full object-cover"
            />
          ) : (
            <User className={`w-4 h-4 ${isAnonymous ? 'text-muted-foreground' : 'text-muted-foreground'}`} />
          )}
        </div>
        <div className="flex flex-col gap-1">
          <span className={`text-sm ${isAnonymous ? 'text-muted-foreground' : 'text-foreground'}`}>
            {displayName}
          </span>
          {isVerifiedClient && !isPreview && (
            <div className="flex items-center gap-1.5 text-[11px] text-blue-500">
              <BadgeCheck className="w-3 h-3 text-blue-500" />
              <span>Verified booking</span>
            </div>
          )}
        </div>
      </div>

    </div>
  )
}
